import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import AppShell from "@/components/AppShell";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PRESETS = [
    "fine_dining", "mexican", "burger_joint", "steakhouse", "seafood",
    "coffee_shop", "bakery", "cocktail_bar", "fast_casual", "tex_mex", "sushi", "pizza",
];

export default function Settings() {
    const { user } = useAuth();
    const [kit, setKit] = useState({
        primary_color: "#f97316",
        accent_color: "#e11d48",
        restaurant_name: "",
        font_family: "Cabinet Grotesk",
        watermark_text: "",
        preferred_preset: "fine_dining",
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/brand-kit").then((r) => {
            const k = r.data || {};
            setKit((prev) => ({ ...prev, ...k }));
        }).finally(() => setLoading(false));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.post("/brand-kit", kit);
            toast.success("Brand kit saved");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <AppShell subtitle="Settings" title="Brand kit & profile.">
            <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                    <div className="glass-card rounded-2xl p-6">
                        <h3 className="font-heading text-lg font-semibold mb-5">Restaurant identity</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Restaurant name</label>
                                <input
                                    value={kit.restaurant_name || ""}
                                    onChange={(e) => setKit({ ...kit, restaurant_name: e.target.value })}
                                    data-testid="settings-restaurant-input"
                                    placeholder="Tacos del Norte"
                                    className="mt-2 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500/60"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Primary color</label>
                                    <div className="mt-2 flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={kit.primary_color}
                                            onChange={(e) => setKit({ ...kit, primary_color: e.target.value })}
                                            data-testid="settings-primary-color"
                                            className="w-12 h-12 rounded-lg bg-black/30 border border-white/10 cursor-pointer"
                                        />
                                        <input
                                            value={kit.primary_color}
                                            onChange={(e) => setKit({ ...kit, primary_color: e.target.value })}
                                            className="flex-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white font-mono text-sm"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Accent color</label>
                                    <div className="mt-2 flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={kit.accent_color}
                                            onChange={(e) => setKit({ ...kit, accent_color: e.target.value })}
                                            data-testid="settings-accent-color"
                                            className="w-12 h-12 rounded-lg bg-black/30 border border-white/10 cursor-pointer"
                                        />
                                        <input
                                            value={kit.accent_color}
                                            onChange={(e) => setKit({ ...kit, accent_color: e.target.value })}
                                            className="flex-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white font-mono text-sm"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Watermark text (optional)</label>
                                <input
                                    value={kit.watermark_text || ""}
                                    onChange={(e) => setKit({ ...kit, watermark_text: e.target.value })}
                                    data-testid="settings-watermark-input"
                                    placeholder="@yourbrand"
                                    className="mt-2 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500/60"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="glass-card rounded-2xl p-6">
                        <h3 className="font-heading text-lg font-semibold mb-5">Default enhancement style</h3>
                        <div className="flex flex-wrap gap-2">
                            {PRESETS.map((p) => (
                                <button
                                    key={p}
                                    type="button"
                                    onClick={() => setKit({ ...kit, preferred_preset: p })}
                                    data-testid={`settings-preset-${p}`}
                                    className={`px-3.5 py-1.5 rounded-full text-sm border capitalize transition ${kit.preferred_preset === p ? "bg-orange-500/15 border-orange-500/40 text-orange-300" : "bg-white/3 border-white/10 text-zinc-400 hover:bg-white/8"}`}
                                >
                                    {p.replace(/_/g, " ")}
                                </button>
                            ))}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving || loading}
                        data-testid="settings-save-btn"
                        className="btn-magic px-5 py-3 rounded-2xl text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {saving ? "Saving..." : "Save brand kit"}
                    </button>
                </div>
                <div className="space-y-6">
                    <div className="glass-card rounded-2xl p-6">
                        <h3 className="font-heading text-lg font-semibold mb-4">Account</h3>
                        <div className="space-y-3 text-sm">
                            <div>
                                <div className="text-xs text-zinc-500 uppercase font-mono tracking-widest">Name</div>
                                <div className="mt-1" data-testid="settings-account-name">{user?.name}</div>
                            </div>
                            <div>
                                <div className="text-xs text-zinc-500 uppercase font-mono tracking-widest">Email</div>
                                <div className="mt-1" data-testid="settings-account-email">{user?.email}</div>
                            </div>
                            <div>
                                <div className="text-xs text-zinc-500 uppercase font-mono tracking-widest">Plan</div>
                                <div className="mt-1 capitalize">{user?.plan === "trial" ? "Free trial" : user?.plan}</div>
                            </div>
                            <div>
                                <div className="text-xs text-zinc-500 uppercase font-mono tracking-widest">Credits</div>
                                <div className="mt-1 text-orange-400 font-mono">{user?.credits_remaining ?? 0}</div>
                            </div>
                        </div>
                    </div>
                    <div className="glass-card rounded-2xl p-6">
                        <h3 className="font-heading text-base font-semibold mb-2">Brand preview</h3>
                        <div className="mt-2 rounded-xl p-5 grid place-items-center" style={{ background: `linear-gradient(135deg, ${kit.accent_color}, ${kit.primary_color})` }}>
                            <div className="text-white font-heading font-bold text-2xl tracking-tight text-center">
                                {kit.restaurant_name || "Your Restaurant"}
                            </div>
                            {kit.watermark_text && (
                                <div className="text-white/80 text-xs font-mono mt-2">{kit.watermark_text}</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
